import React, { ReactElement, useState } from 'react';
import { useRouter } from 'next/router';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import AppLayout from 'components/layouts/AppLayout';
import { TNamespaceEnum } from 'types/types';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
    StateInterface,
    ValidateValueType,
    ValueOnChangeRemoveType,
} from 'components/interfaces/GeneralInterface';
import { FieldTypeApiEnum } from 'submodules/api_middleware';
import EditForm from 'components/editForm/EditForm';
import { OnActionType } from 'components/interfaces/EditFormInterface';
import { getMinDate, isValidDate } from 'utils/helpers/datetime';
import { getLocaleDateString } from 'utils/helpers/getLocalDateString';
import {
    parseCzechBirthNumber,
    slashCzechBirthNumber,
    validateEmail,
} from 'utils/index';
import { fetcherFull, MethodEnum } from 'utils/helpers/browser/newFetcher';
import { getTokenCookie } from 'utils/helpers/browser/cookies';
import { getJwtUserName, TokenPurpose } from 'utils/helpers/cookies.shared';
import { InfoType } from 'components/tables/filters/SpecialInput';
import { performRecoveryByLogin } from 'services/personRecovery.service';
import styles from './PersonRecoveryPage.module.scss';

export const RecoveryType = {
    ByIs1Login: 'RecoveryByLogin',
    WithoutEmail: 'RecoveryWithoutEmail',
};

const PersonRecoveryPage = (): ReactElement => {
    const [success, setSuccess] = useState(false);
    const router = useRouter();

    const recoveryType = router.query?.recoveryType as string;

    // Translation
    const { t } = useTranslation();

    // Login
    const token = getTokenCookie(TokenPurpose.Access);
    const jwtUserName = token ? getJwtUserName(token) : null;

    function isLoggedIn(): boolean {
        return !!jwtUserName;
    }

    /** Creates a validation function for a specific recovery type */
    function recoveryTypeIs(recoveryType: string): (state: any) => boolean {
        return (state: any) => state.recoveryType == recoveryType;
    }

    /** Sends request to the backend */
    async function sendRequest(data: any, url: string): Promise<any> {
        const response = await fetcherFull<any>(
            url,
            MethodEnum.POST,
            undefined,
            data
        );

        if (response.error) {
            console.error(response);
        }

        return response;
    }

    // On form submit
    const onRecoverySubmit: OnActionType = async (state) => {
        const confirmationUrl = `${location.origin}/person-recovery/confirm`;

        // Recovery by email
        if (state['recoveryType'] == RecoveryType.WithoutEmail) {
            const data: any = {
                data: {
                    new_email: state['new_email_no_mail'],
                    first_name: state['first_name_no_mail'],
                    last_name: state['surname_no_mail'],
                    birthdate: new Date(
                        state['birth_year_no_mail']
                    ).toISOString(),
                    national_identifier: state['national_identifier_no_mail'],
                    member_id: state['membership_id_no_mail'],
                    linked: isLoggedIn(),
                    confirmation_url: confirmationUrl,
                },
            };
            const res = await sendRequest(
                data,
                '/api/person_recovery/without_email'
            );
            if (res && !res.error) setSuccess(true);
        } else if (state['recoveryType'] === RecoveryType.ByIs1Login) {
            const username = state['username'];
            const password = state['password'];

            if (!username || !password) {
                console.error('Missing recovery data');
                return;
            }

            try {
                await performRecoveryByLogin(
                    username,
                    password,
                    isLoggedIn(),
                    router
                );
                setSuccess(true);
            } catch (err) {
                console.error('Recovery failed:', err);
            }
        } else {
            console.error('Not implemented state ', state['recoveryType']);
        }
    };

    // Form groups and fields
    const fields: GroupEditFieldInterface[] = [
        // ---- Choose the recovery type ----
        {
            title: getTranslation(
                t,
                TNamespaceEnum.personRecovery,
                'recoveryTypeTitle'
            ),
            fields: [
                {
                    name: 'recoveryType',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'recoveryType'
                    ),
                    type: FieldTypeApiEnum.RADIO,
                    size: FilterSize.FULL,
                    value: recoveryType ? recoveryType : null,
                    values: [
                        RecoveryType.ByIs1Login,
                        RecoveryType.WithoutEmail,
                    ].map(
                        (type): Option => ({
                            label: getTranslation(
                                t,
                                TNamespaceEnum.personRecovery,
                                `recoveryTypes.${type}`
                            ),
                            value: type,
                        })
                    ),
                    labelSize: LabelSize.TOP,
                    required: true,
                    className: 'wrapper-auto',
                },
            ],
        },

        // ---- Various recovery types ----
        // Recovery by login
        {
            title: getTranslation(
                t,
                TNamespaceEnum.personRecovery,
                `${RecoveryType.ByIs1Login}.title`
            ),
            fields: [
                {
                    name: 'login_logged_in',
                    label: t(
                        getTranslationKey(
                            TNamespaceEnum.personRecovery,
                            'recoveryForLoggedInAccount'
                        ),
                        {
                            user: jwtUserName,
                        }
                    ),
                    type: FieldTypeApiEnum.INFO,
                    infoType: InfoType.INFO,
                    displayIfSuccess: () => isLoggedIn(),
                },
                {
                    name: 'username',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.username'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: recoveryTypeIs(RecoveryType.ByIs1Login),
                },
                {
                    name: 'password',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.password'
                    ),
                    type: FieldTypeApiEnum.PASSWORD,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: recoveryTypeIs(RecoveryType.ByIs1Login),
                },
            ],
            displaySectionIfSuccess: recoveryTypeIs(RecoveryType.ByIs1Login),
        },

        // Recovery without email
        {
            title: getTranslation(
                t,
                TNamespaceEnum.personRecovery,
                `${RecoveryType.WithoutEmail}.title`
            ),
            fields: [
                {
                    name: 'no_mail_logged_in',
                    label: t(
                        getTranslationKey(
                            TNamespaceEnum.personRecovery,
                            'recoveryForLoggedInAccount'
                        ),
                        {
                            user: jwtUserName,
                        }
                    ),
                    type: FieldTypeApiEnum.INFO,
                    infoType: InfoType.INFO,
                    displayIfSuccess: () => isLoggedIn(),
                },
                {
                    name: 'new_email_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.newEmail'
                    ),
                    value: isLoggedIn() ? jwtUserName : null,
                    type: FieldTypeApiEnum.EMAIL,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    disabledIfSuccess: () => isLoggedIn(),
                    validate: (value: ValidateValueType) =>
                        validateEmail(t, String(value)),
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
                {
                    name: 'first_name_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.firstName'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
                {
                    name: 'surname_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.surname'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
                {
                    name: 'birth_year_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.birthYear'
                    ),
                    type: FieldTypeApiEnum.DATE,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    min: getMinDate(),
                    max: new Date().getTime(),
                    required: true,
                    validate: (value: ValidateValueType) => {
                        const now = new Date();
                        now.setHours(0, 0, 0, 0);
                        const date = new Date(value as string);

                        return isValidDate(date)
                            ? value
                                ? date.getTime() < now.getTime()
                                    ? undefined
                                    : t('validations.dateMustBeInPast')
                                : undefined
                            : t('validations.dateUseFormat', {
                                  format: getLocaleDateString(),
                              });
                    },
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
                {
                    name: 'national_identifier_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.nationalIdentifier'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    validate: (value: ValidateValueType) => {
                        const validationRes = parseCzechBirthNumber(
                            String(value)
                        );

                        // Invalid id
                        if (!validationRes.valid)
                            return t(validationRes.errorMessageKey);

                        return undefined;
                    },
                    onChange: (
                        value: ValueOnChangeRemoveType,
                        state: StateInterface
                    ) => {
                        const strVal = String(value);
                        state.national_identifier_no_mail =
                            slashCzechBirthNumber(true, strVal);
                        return state;
                    },
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
                {
                    name: 'membership_id_no_mail',
                    label: getTranslationKey(
                        TNamespaceEnum.personRecovery,
                        'form.membershipId'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    required: true,
                    validate: (value: ValidateValueType) => {
                        if (!value) return undefined;
                        if (value.toString().length != 8)
                            return t('validations.memberIdNotValid');
                        return undefined;
                    },
                    displayIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
                },
            ],
            displaySectionIfSuccess: recoveryTypeIs(RecoveryType.WithoutEmail),
        },
    ];

    return (
        <AppLayout
            title={getTranslation(
                t,
                TNamespaceEnum.personRecovery,
                'pageTitle'
            )}
            pageTitle={{
                primaryBoldText: getTranslation(
                    t,
                    TNamespaceEnum.personRecovery,
                    'personRecovery'
                ),
                isDark: true,
            }}
            hideUserMenu
            showDisciplineMenu
            hideLoginAs
            changeDisciplineWithoutReload={true}
            showLangMenu={true}
        >
            {!success ? (
                <div>
                    <div className={styles.infoText}>
                        <span>
                            {getTranslation(
                                t,
                                TNamespaceEnum.personRecovery,
                                'recoveryInfo'
                            )}
                        </span>
                    </div>
                    <div className={styles.formWrapper}>
                        <EditForm
                            fields={fields}
                            buttonTextKey={getTranslationKey(
                                TNamespaceEnum.personRecovery,
                                'recover'
                            )}
                            action={onRecoverySubmit}
                        />
                    </div>
                </div>
            ) : (
                <div className={styles.infoText}>
                    <span>
                        {getTranslation(
                            t,
                            TNamespaceEnum.personRecovery,
                            'recoveryCreatedSuccessfully'
                        )}
                    </span>
                </div>
            )}
        </AppLayout>
    );
};

export default PersonRecoveryPage;
